package fr.arcplex.sooner.infrastructure

import fr.arcplex.network.cache.currentTimeMillis
import fr.arcplex.network.logging.Logger

import androidx.compose.runtime.*
import fr.arcplex.sooner.coherence.CoherenceThemeState
import fr.arcplex.network.template.*
import fr.arcplex.network.NetworkConfig
import fr.arcplex.network.SoonerApi
import fr.arcplex.network.SoonerApiFactory
import fr.arcplex.network.RendererKind
import fr.arcplex.network.model.Branding
import fr.arcplex.network.model.CoherenceFilmInfo
import fr.arcplex.network.model.PlaybackSource
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.model.ScreenAction
import fr.arcplex.network.model.ScreenKind
import fr.arcplex.network.model.searchInputItem
import fr.arcplex.sooner.webui.Page
import fr.arcplex.sooner.webui.toScreen
import fr.arcplex.sooner.VideoInfo
import fr.arcplex.sooner.useCoherence
import kotlinx.coroutines.*

import kotlin.random.Random

import fr.arcplex.network.reporting.ReportEventType
private const val SEEK_JUMP_THRESHOLD_MS = 3_000L

/** Startup phases shown by the boot screen. */
enum class BootState {
    IDLE,
    LOADING_THEME,
    LOADING_ROOT,
    READY,
    ERROR
}

class PageManager(
    private val identity: PlatformIdentity,
    config: NetworkConfig = NetworkConfig(useCoherence = useCoherence, allowInvalidCertificates = true),
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main + SupervisorJob())
) {
    private val api: SoonerApi = SoonerApiFactory.create(config, identity)

    private val reporter = SoonerApiFactory.createReporter(config, identity)

    /** Identifies one playback so the events it produces can be correlated. */
    private var playbackSessionId: String? = null
    private var playbackIdProgramme: String? = null
    private var playbackStarted = false
    private var lastKnownPositionMs = 0L

    init {
        // Replays whatever was queued before the app was closed. The reporter owns its own
        // scope, so PageManager.stop() cannot cancel a send that is already in flight.
        reporter.start()
    }

    val pages = mutableMapOf<String, Page>()
    var currentPage by mutableStateOf<Page?>(null)
        private set
    var currentVideo by mutableStateOf<PlaybackSource?>(null)
        private set
    var currentVideoInfo by mutableStateOf<VideoInfo?>(null)
        private set
    var error by mutableStateOf<String?>(null)
        private set
    var pageKind by mutableStateOf<ScreenKind?>(null)
        private set
    var coherenceFilmInfo by mutableStateOf<CoherenceFilmInfo?>(null)
        internal set
    var coherenceFallbackPage by mutableStateOf<Page?>(null)
        internal set
    var onFilmSelectedListener: ((String?) -> Unit)? = null
    var onVideoSelectedListener: ((RailItem) -> Unit)? = null
    var onPageLoadedListener: ((Page) -> Unit)? = null

    // Boot phases are exposed so the splash views stay stateless.
    var bootState by mutableStateOf(BootState.IDLE)
        private set
    var bootError by mutableStateOf<String?>(null)
        private set

    // Burger menu is a modal layer: the app Back handler must close it before
    // navigating back, so its open state lives here rather than in each view.
    var menuOpen by mutableStateOf(false)
        private set

    /** Which backend renderer the film screen should use. */
    val isCoherence: Boolean get() = api.renderer == RendererKind.COHERENCE

    fun toggleMenu() {
        menuOpen = !menuOpen
    }

    fun setMenu(open: Boolean) {
        menuOpen = open
    }

    fun closeMenu() {
        menuOpen = false
    }

    private data class ScreenState(
        val page: Page,
        val pageKind: ScreenKind,
        val coherenceFilmInfo: CoherenceFilmInfo?,
        val coherenceFallbackPage: Page?
    )

    private val history = mutableListOf<ScreenState>()
    var canGoBack by mutableStateOf(false)
        private set

    fun onFilmSelected(id_programme: String?) {
        onFilmSelectedListener?.invoke(id_programme)
    }

    fun onVideoSelected(item: RailItem) {
        onVideoSelectedListener?.invoke(item)
    }

    private fun pushHistory() {
        if (currentPage != null && pageKind != null) {
            history.add(ScreenState(currentPage!!, pageKind!!, coherenceFilmInfo, coherenceFallbackPage))
            canGoBack = true
        }
    }

    fun goBack() {
        if (history.isNotEmpty()) {
            menuOpen = false
            val previous = history.removeAt(history.size - 1)
            currentPage = previous.page
            pageKind = previous.pageKind
            coherenceFilmInfo = previous.coherenceFilmInfo
            coherenceFallbackPage = previous.coherenceFallbackPage
            currentVideo = null
            currentVideoInfo = null
            canGoBack = history.isNotEmpty()
        }
    }

    fun onPlaybackStarted() {
        if (playbackStarted) return
        playbackStarted = true
        val session = playbackSessionId ?: newPlaybackSessionId().also { playbackSessionId = it }
        reporter.report(ReportEventType.VIDEO_STARTED, playbackPayload(session))
    }

    fun onPlaybackStopped() {
        if (!playbackStarted) return
        playbackStarted = false
        val session = playbackSessionId ?: return
        reporter.report(
            ReportEventType.VIDEO_STOPPED,
            playbackPayload(session) + ("position_ms" to lastKnownPositionMs.toString())
        )
        playbackSessionId = null
        playbackIdProgramme = null
        lastKnownPositionMs = 0L
    }

    /**
     * The web player calls its seek callback on every position change (~1/s) rather than only
     * on a real seek, so anything below [SEEK_JUMP_THRESHOLD_MS] is not a seek.
     */
    fun onPlaybackSeek(positionMs: Long) {
        val delta = positionMs - lastKnownPositionMs
        lastKnownPositionMs = positionMs
        if (!playbackStarted) return
        if (delta > -SEEK_JUMP_THRESHOLD_MS && delta < SEEK_JUMP_THRESHOLD_MS) return
        val session = playbackSessionId ?: return
        reporter.report(
            ReportEventType.VIDEO_SEEK,
            playbackPayload(session) + ("position_ms" to positionMs.toString())
        )
    }

    private fun playbackPayload(session: String): Map<String, String> {
        val payload = mutableMapOf("session_id" to session)
        playbackIdProgramme?.let { payload["id_programme"] = it }
        return payload
    }

    private fun newPlaybackSessionId(): String =
        "${currentTimeMillis()}-${Random.nextLong().toString(16)}"

    fun start() {
        scope.launch {
            bootState = BootState.LOADING_THEME
            bootError = null
            try {
                // The port owns the backend choice (legacy vs Coherence); branding
                // comes from the backend behind it, cached when warm.
                CoherenceThemeState.apply(api.cachedBranding())
                api.refreshBranding().getOrNull()?.let { CoherenceThemeState.apply(it) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // The theme has defaults: a theme failure must not block startup.
                Logger.e("BOOT", "theme refresh failed: ${e.message}")
            }
            bootState = BootState.LOADING_ROOT
            fetchRootPage()
        }
    }

    /** Retries the whole startup sequence after a boot failure. */
    fun retryBoot() {
        bootError = null
        error = null
        start()
    }

    fun fetchFilm(id_film: String) {
        scope.launch {
            try {
                error = null
                val film = api.fetchFilm(id_film).getOrElse { throw it }
                if (isCoherence && film.coherence != null) {
                    // refreshPage pushes the screen being left onto the history stack;
                    // the new coherence state must only be assigned afterwards so the
                    // snapshot captures the previous film for goBack()
                    refreshPage(id_film, currentPage ?: emptyFilmPage(), ScreenKind.FILM)
                    coherenceFilmInfo = film.coherence
                    coherenceFallbackPage = Page.from(film.screen)
                } else {
                    // Legacy renderer, or the Coherence payload is unusable: show the
                    // legacy film page, exactly like before the port.
                    refreshPage(id_film, Page.from(film.screen), film.screen.kind)
                    coherenceFilmInfo = null
                    coherenceFallbackPage = null
                }
            } catch (e: Exception) {
                error = e.message
            }
        }
    }

    fun playVideo(item: RailItem) {
        scope.launch {
            try {
                error = null

                pushHistory()

                playbackSessionId = newPlaybackSessionId()
                playbackIdProgramme = null
                playbackStarted = false
                lastKnownPositionMs = 0L

                val source = api.resolvePlayback(item).getOrElse { throw it }
                playbackIdProgramme = source.payload["id_programme"]
                currentVideoInfo = currentPage?.getVideoInformation() ?: coherenceFallbackPage?.getVideoInformation()
                currentVideo = source
            } catch (e: Exception) {
                error = e.message
            }
        }
    }

    private suspend fun fetchRootPage() {
        try {
            error = null
            val screen = api.fetchRoot().getOrElse { throw it }
            refreshPage(screen.id ?: "", Page.from(screen), screen.kind)
            bootState = BootState.READY
        } catch (e: Exception) {
            val msg = "Error fetching root page: ${e.message}"
            fr.arcplex.network.logging.Logger.e("FILMO API", msg)
            bootError = msg
            bootState = BootState.ERROR
            error = msg
            e.printStackTrace()
        }
    }

    fun refreshPage(id: String, content: Page, kind: ScreenKind) {
        pushHistory()
        menuOpen = false
        pages[id] = content
        pageKind = kind
        currentPage = content
        onPageLoadedListener?.invoke(content)
    }

    /**
     * Routes a click on a rail/menu item through the port, which owns the
     * backend's action semantics (navigation vs playback).
     */
    fun dispatchItemAction(item: RailItem) {
        when (val action = api.resolveAction(item, currentPage?.toScreen())) {
            ScreenAction.ToggleMenu -> closeMenu()
            is ScreenAction.OpenFilm -> fetchFilm(action.filmId)
            is ScreenAction.Play -> onVideoSelected(action.item)
            is ScreenAction.OpenScreen -> fetchPage(action.url)
            ScreenAction.Ignore -> Unit
        }
    }

    /** Loads a server page from an action URL (menu entries, promotional links). */
    fun fetchPage(actionUrl: String) {
        scope.launch {
            try {
                error = null
                val screen = api.fetchScreen(actionUrl).getOrElse { throw it }
                refreshPage(screen.id ?: actionUrl, Page.from(screen), screen.kind)
                coherenceFilmInfo = null
                coherenceFallbackPage = null
            } catch (e: Exception) {
                error = e.message
            }
        }
    }

    private var searchJob: Job? = null

    /**
     * Runs one search request for a query typed in [rail].
     *
     * Only the newest request is kept: the previous one is cancelled before a
     * new one starts, and the port additionally drops answers whose
     * `str_recherche` is not the query that produced them. The global loading
     * spinner is intentionally untouched: typing must not blank the page.
     */
    fun search(rail: Rail, query: String, validate: Boolean = false) {
        val actionUrl = rail.searchInputItem()?.action?.url ?: return
        val page = currentPage ?: return
        searchJob?.cancel()
        searchJob = scope.launch {
            try {
                val result = api.search(actionUrl, query, validate).getOrElse { throw it }
                val target = result.targetId
                if (target == null) {
                    Logger.d("SEARCH", "answer ignored for query='$query'")
                    return@launch
                }
                val applied = page.updateRail(target, result.items)
                Logger.d("SEARCH", "applied=$applied target=$target items=${result.items.size}")
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // A failed search must not replace the page with the error screen.
                Logger.e("SEARCH", "search failed: ${e.message}")
            }
        }
    }

    private fun emptyRail() = Rail(id = "", uid = "", type = "", height = 60f, focusable = true)

    private fun emptyFilmPage() =
        Page(null, "FILM", "", null, "#0A0E17", emptyRail(), emptyRail(), emptyList())

    fun stop() {
        // If the user leaves while a video is playing, that playback ends here and must be
        // reported. The reporter is deliberately left running: it owns a separate scope so
        // an in-flight send is not cancelled along with this scope.
        onPlaybackStopped()
        scope.cancel()
    }
}
